import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Flip } from "gsap/Flip";
import { IsTouchDevice, QuickTo } from "./Helpers";

export default class BookAnimations {
  constructor() {
    if(!document.querySelector('.page-template-page-book')) return;
    this.isMobile = window.matchMedia('(max-width: 992px)').matches;
    this.pageContainer = document.querySelector('.page-template-page-book');
    gsap.registerPlugin(ScrollTrigger);
    gsap.registerPlugin(Flip);
    this._init();
  }

  _init() {
    this._teamSectionAnimations();
    this._topSectionAnimations();
    this._introSectionAnimations();
    this._accessSectionAnimations();
    this._flipbookAnimations();

    this.defaultParallax();

    window.addEventListener('mousemove', event => {
      const imgs = document.querySelectorAll('img[data-rotate]');
      this.rotateOnMouseMove(event, imgs);
    });
  }

  _topSectionAnimations() {
    this.topImages = this.pageContainer.querySelector('.book-top__image');
    const imgs = this.topImages.querySelectorAll('img[data-head]');

    const setTopOptions = () => {
      this.setRotateConfigs(imgs[0], 46.5, 18.6, -.015);
      this.setRotateConfigs(imgs[1], 55.5, 20);
    }

    const downloadBtnParallax = () => {
      const btn = this.pageContainer.querySelector('.book-top__download');

      const btnAnim = progress => {
        return {
          rotate: progress * 35,
          y: progress * -350,
          x: progress * 200,
        }
      }

      const options = {
        start: '200px top',
      }

      this.scrollParallax(btn, btnAnim, options);
    }

    const headsAnim = () => {
      imgs.forEach(img => {
        gsap.to(img, {
          rotate: img.rotateMultiplier * 800,
          duration: 1,
          yoyo: true,
          delay: imgs[1] === img ? .2 : 0,
          repeat: -1
        });
      })
    }

    setTopOptions();
    headsAnim();
    downloadBtnParallax();
  }

  _introSectionAnimations() {
    this.introImages = this.pageContainer.querySelector('.book-intro__image');

    const setIntroImgsInitialStyles = () => {
      gsap.set(this.introImages, {
        y: 200,
        autoAlpha: 0,
      });
    }

    const setIntroOptions = () => {
      const imgs = this.introImages.querySelectorAll('img[data-rotate]');

      this.setRotateConfigs(imgs[0], 53.5, 96, -.02);
      this.setRotateConfigs(imgs[1], 46, 71, .008);
    }

    const introParallax = () => {
      const introImg = this.introImages.querySelectorAll('[data-rotate]');

      introImg.forEach(el => {
        let multiplier = el.rotateMultiplier;
        let direction = 1;
        let accordionSum = el == introImg[0] ? 20 : 0

        const imgAnim = progress => {
          return {rotate: progress * direction * (multiplier * 2000) + accordionSum}
        }

        this.scrollParallax(el, imgAnim);
      })
    }

    setIntroImgsInitialStyles();
    setIntroOptions();
    introParallax();
  }

  _accessSectionAnimations() {
    const accessBookSection = document.querySelector('.book-access');
    const refBoundingRectTop = accessBookSection.getBoundingClientRect().top;

    const bookMockupHolder = document.querySelectorAll('.book-access__mockup');
    const bookMockup = document.querySelectorAll('.book-access__mockup img');
    const fish = document.querySelectorAll('.book-access__fish > img');

    gsap.set(fish[0], {
      x: -100,
      opacity: 0,
    })

    gsap.set(fish[1], {
      x: 100,
      opacity: 0
    })

    const mouseParallax = () => {
      window.addEventListener('mousemove', event => {
        if(IsTouchDevice()) return;

        const posX = event.clientX - window.innerWidth *.8;
        const moveXBack = QuickTo(bookMockup[1], 'x');
        const rotateY = QuickTo(bookMockupHolder, 'rotationY');

        rotateY(posX * .02);
        moveXBack(posX * -.01);
      });
    }

    const mockupAnim = () => {
      const duration = 2;
      const distance = 20;
      return gsap.timeline({repeat: -1, yoyo: true, ease: "power2.inOut",})
        .to(bookMockup[0], {
          opacity: .5,
          scaleX: .7,
          y: distance,
          duration
        })
        .to(bookMockup[1], {
          y: -distance,
          duration
        }, "=<")
        .to(bookMockup[2], {
          y: -distance,
          duration

        }, "=<")
    }

    const swimmingAnim = (batch) => {
      return gsap.to(batch, {
        y: 10,
        repeat: -1,
        yoyo: true,
        ease: "back.inOut(4)",
        duration: 2,
        stagger: .7
      })
    }

    const fishInView = (batch) => {
      ScrollTrigger.batch(batch, {
        trigger: accessBookSection,
        interval: 0.1,
        start: "top 70%",

        onEnter: batch => gsap.to(batch, {
          autoAlpha: 1,
          x:0,
          duration: 2,
          onComplete: () => {
            swimmingAnim(batch);
          }
        }),
      });
    }

    fish.forEach(el => {
      let direction = el === fish[0] ? 1 : -1;

      const startPos = refBoundingRectTop + -100;

      const options = {
        start: `${startPos} top`,
      }

      const leaveFishAnim = progress => {
        return {
          left: progress * window.innerWidth * .3 * direction,
        }
      }

      this.scrollParallax(el, leaveFishAnim, options);
    });

    const downloadBtnParallax = () => {
      const btns = accessBookSection.querySelectorAll('.book-access__btns > div');
      const startPos = refBoundingRectTop + accessBookSection.offsetHeight * .3;

      btns.forEach(btn => {
        const btnAnim = progress => {
          return {
            rotate: progress * (btns[0] === btn ? 35 : -35),
            y: progress * (btns[0] === btn ? -350 : -200),
            x: progress * (btns[0] === btn ? 200 : -100),
          }
        }

        const options = {
          start: `${startPos} top`,
        }

        this.scrollParallax(btn, btnAnim, options);
      })
    }

    mockupAnim();
    fishInView(fish)
    mouseParallax();
    downloadBtnParallax();
  }

  _teamSectionAnimations() {
    const teamContainer = document.querySelector('.book-team');
    const holder = teamContainer.querySelector('.book-team__holder');
    const items = teamContainer.querySelectorAll('.book-team__item');
    const gutter = () => this.isMobile ? 20 : 40;
    let photos = teamContainer.querySelectorAll(`.book-team__item${!this.isMobile ? ':not(:first-child)': ''}`);

    const setWidths = () => {
      const width = !this.isMobile ? holder.offsetWidth / 4 - gutter() * 3 / 4 : 300;
      const holderWidth = width * items.length + (gutter() * (items.length - 1));
			console.log( holderWidth );
      items.forEach(el => el.style.width = width + 'px');
      holder.style.width = holderWidth + 'px';

      return holderWidth;
    }

    const scrollWidth = setWidths();

    let scrollTween = gsap.to(photos, {
      x: (i) => -(photos[i].offsetWidth + gutter()) * i,
      rotate: (i) => i === 0 ? 0 : i % 2 === 0 ? gsap.utils.random(-5, 1) : gsap.utils.random(1, 5),
      duration: (i) => 0.5 * i,
      ease: "none",
      scrollTrigger: {
        trigger: teamContainer,
        pin: true,
        scrub: 0.1,
        end: `+=${scrollWidth * 1.5} bottom`,
        invalidateOnRefresh: true,
      }
    });
  }

  _flipbookAnimations() {
    const flipbookContainer = document.querySelector('.book-flipbook');
    const flipbookIllustrations = document.querySelectorAll('.book-flipbook__illustrations img');

    gsap.from(flipbookIllustrations, {
      top: i => i === 0 && "10%",
      left: i => i === 1 && "10%",
      right: i => i === 2 && "10%",
      opacity: 0,
      duration: 1,
      scrollTrigger: {
        trigger: '.book-flipbook__holder',
        start: () => "top 50%",
      }
    });

    gsap.from('.book-flipbook__book', {
      scale: .7,
      opacity: 0,
      duration: 1,
      scrollTrigger: {
        trigger: '.book-flipbook__holder',
        start: () => "top 70%",
      }
    });

    this._flipbookModaltoggle();
  }

  _gsapFlipBook(modal) {
    const book = document.querySelector(".book-flipbook__book");
		if( !book ) return;
    const cover = book.querySelector('img');
    const coverFake = book.querySelector(':scope > div:first-of-type');
    const originalHolder = document.querySelector(".book-flipbook__book-holder");
    const modalHolder = document.querySelector(".book-holder");
    const iframe = modal.querySelector('iframe');

    if(book.classList.contains('full')) {
      book.classList.remove('full');
      return
    }

    book.setAttribute('open', true);
    const state = Flip.getState(book);


    setTimeout(() => {
      modalHolder.insertAdjacentElement('afterbegin', book);
      book.classList.add('full');

      Flip.from(state, {
        duration: 0.6,
        scale: true,
        toggleClass: "flipping",
        ease: "power1.inOut",
        onStart: () => {
          gsap.to(".book-flipbook__pages", {
            backgroundImage: "linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0) 100%)",
            backgroundColor: "#fff",
            duration: 1,
            onComplete: () => {
              setTimeout(() => {
                gsap.set(".book-flipbook__pages", {clearProps: 'all'})
              }, 1000);
            }
          });

          gsap.to(book, {
            opacity: 0,
            delay: .6,
            duration: 1,
            clearProps: 'all',
            onComplete: () => {
              gsap.set(cover, {display: "none"});
              book.removeAttribute('open');
              originalHolder.insertAdjacentElement('afterbegin', book);
              gsap.set([cover], {clearProps: "all"})
            }
          });
        },
      })
    }, 700);
  }

  _reloadIframe(modal) {
    const iframe = modal.querySelector('iframe');
    modal.appendChild(iframe);
  }

  _flipbookModaltoggle() {
    const btns = document.querySelectorAll('.moth-book [aria-controls]');
    btns.forEach(btn => {
      btn.addEventListener('click', e => {
        const targetID = btn.getAttribute('aria-controls');
        const target = document.getElementById(targetID);
        const sameTriggers = document.querySelectorAll(`[aria-controls=${targetID}]`);

        if(target.getAttribute('aria-hidden') == "true") {
          target.setAttribute('aria-hidden', false);
        } else {
          target.setAttribute('aria-hidden', true);
          this._reloadIframe(target);
        }

        sameTriggers.forEach(el => {
          if(el.getAttribute('aria-expanded') == "true") {
            el.setAttribute('aria-expanded', false);
          } else {
            el.setAttribute('aria-expanded', true);
          }
        });

        this._gsapFlipBook(target);
      });
    })
  }

  defaultParallax() {
    const parallaxElement = this.pageContainer.querySelectorAll('[data-parallax-element]:not([data-parallax-element="custom"])');

    parallaxElement.forEach(el => {
      this.scrollParallax(el);
    });
  }

  scrollParallax(el, customAnimation = false, customOptions = {}) {
    const scrollRef = el.closest('[data-scroll-ref]');

    if(!scrollRef) return;

    const refBoundingRectTop = scrollRef.getBoundingClientRect().top;
    const startPos = refBoundingRectTop < window.innerHeight / 2 ? refBoundingRectTop - 10 : window.innerHeight / 2;
    const elementAnimation = el.getAttribute('data-parallax-element');

    ScrollTrigger.create({
      scrollRef,
      start: `${refBoundingRectTop}px ${startPos}px`,
      end: `${refBoundingRectTop + scrollRef.offsetHeight}px top`,
      scrub: true,
      onUpdate: self => {
        const progress = self.progress.toFixed(3);

        if(!Number(elementAnimation) && customAnimation) {
          const animation = customAnimation(progress);

          if(typeof animation !== 'object' && !Object.keys(animation)) return;

          gsap.to(el, animation);

          return;
        }

        gsap.to(el, {y: progress * (Number(elementAnimation) || 100)});
      },
      ...customOptions
    });
  }

  setRotateConfigs(el,x, y, multiplier = .015) {
    el.rotateMultiplier = multiplier;
    el.style.transformOrigin = `${x}% ${y}%`;
  }

  rotateOnMouseMove(event, el) {
    if(IsTouchDevice()) return;

    const centerX = event.clientX - (window.innerWidth / 2);

    gsap.utils.toArray(el).forEach(item => {
      if(!item.hasAttribute('data-rotate')) return

      const rotate = QuickTo(item, 'rotation');

      rotate(centerX  * item.rotateMultiplier);
    })
  }
}

